import { useMemo, useState } from 'react';
import { downloadCsv, formatDate, formatMarketCap, formatPrice, formatShares } from '../lib/format';
import { resample } from '../lib/marketCap';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/** Market cap by date — the numbers behind the chart, at whatever granularity is readable. */
export default function MarketCapTable({ ticker, series }) {
  const [frequency, setFrequency] = useState('daily');
  const [newestFirst, setNewestFirst] = useState(true);

  const rows = useMemo(() => {
    const sampled = resample(series, frequency);
    return newestFirst ? [...sampled].reverse() : sampled;
  }, [series, frequency, newestFirst]);

  function exportCsv() {
    downloadCsv(
      `${ticker}-market-cap-${frequency}.csv`,
      ['Date', 'Close', 'Shares outstanding', 'Market cap', 'Share count from', 'Shares as of'],
      rows.map((r) => [r.date, r.close, Math.round(r.shares), Math.round(r.marketCap), r.form, r.asOfDate]),
    );
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Market cap by date</h2>
        <p className="card-note">{rows.length.toLocaleString()} rows</p>
        <div className="card-actions">
          <label className="toggle">
            <span className="field-label" style={{ textTransform: 'none', letterSpacing: 0 }}>Frequency</span>
            <select
              className="select"
              style={{ minWidth: 110, padding: '5px 8px', fontSize: 12 }}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn-quiet" onClick={() => setNewestFirst((v) => !v)}>
            {newestFirst ? 'Newest first ↓' : 'Oldest first ↑'}
          </button>
          <button type="button" className="btn-quiet" onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Close</th>
              <th className="num">Shares outstanding</th>
              <th className="num">Market cap</th>
              <th>Share count from</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <td className="mono">{formatDate(row.date)}</td>
                <td className="num">{formatPrice(row.close)}</td>
                <td className="num">{formatShares(row.shares)}</td>
                <td className="num">{formatMarketCap(row.marketCap)}</td>
                <td className="hint">
                  {row.form} · as of {formatDate(row.asOfDate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {frequency !== 'daily' && (
        <div className="card-foot">
          <p className="hint" style={{ margin: 0 }}>
            Weekly and monthly rows show the last trading day in each period.
          </p>
        </div>
      )}
    </section>
  );
}
